import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { tenantGroupsApi, type TenantGroup, type TenantGroupMember } from "@/api/tenant-groups";
import { tenantUsersApi, type TenantUser } from "@/api/tenant-users";
import { entraSyncApi, type EntraDirectoryGroup } from "@/api/entra-sync";
import { queryKeys } from "@/lib/query-keys";
import {
  UsersRound,
  Plus,
  Trash2,
  Download,
  Loader2,
  Check,
  Search,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Cloud,
  HardDrive,
  UserPlus,
  X,
} from "lucide-react";

export function GroupManagement() {
  const { tenantId, isAdmin } = useTenant();
  const queryClient = useQueryClient();
  const [showImport, setShowImport] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [selectedImports, setSelectedImports] = useState<Set<string>>(new Set());
  const [importSearch, setImportSearch] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [newGroupDesc, setNewGroupDesc] = useState("");
  const [addMemberGroupId, setAddMemberGroupId] = useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");

  const { data: groups = [], isLoading } = useQuery({
    queryKey: queryKeys.tenantGroups.byCompany(tenantId),
    queryFn: () => tenantGroupsApi.list(tenantId),
  });

  const { data: entraGroups = [], isLoading: entraLoading } = useQuery({
    queryKey: queryKeys.entra.groups(tenantId),
    queryFn: () => entraSyncApi.listGroups(tenantId),
    enabled: showImport,
  });

  const { data: members = [] } = useQuery({
    queryKey: queryKeys.tenantGroups.members(expandedGroup ?? ""),
    queryFn: () => tenantGroupsApi.listMembers(tenantId, expandedGroup!),
    enabled: !!expandedGroup,
  });

  const { data: allUsers = [] } = useQuery({
    queryKey: queryKeys.tenantUsers.byCompany(tenantId),
    queryFn: () => tenantUsersApi.list(tenantId),
  });

  const createMutation = useMutation({
    mutationFn: () =>
      tenantGroupsApi.create(tenantId, {
        name: newGroupName,
        description: newGroupDesc || undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.byCompany(tenantId) });
      setShowCreateGroup(false);
      setNewGroupName("");
      setNewGroupDesc("");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (groupId: string) => tenantGroupsApi.delete(tenantId, groupId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.byCompany(tenantId) }),
  });

  const importMutation = useMutation({
    mutationFn: (selected: EntraDirectoryGroup[]) =>
      entraSyncApi.importGroups(
        tenantId,
        selected.map((g) => ({ externalId: g.externalId, displayName: g.displayName })),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.byCompany(tenantId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantUsers.byCompany(tenantId) });
      setShowImport(false);
      setSelectedImports(new Set());
    },
  });

  const syncMutation = useMutation({
    mutationFn: (groupId: string) => entraSyncApi.syncGroup(tenantId, groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.byCompany(tenantId) });
      if (expandedGroup) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenantGroups.members(expandedGroup),
        });
      }
    },
  });

  const addMemberMutation = useMutation({
    mutationFn: ({ groupId, userIds }: { groupId: string; userIds: string[] }) =>
      tenantGroupsApi.addMembers(tenantId, groupId, userIds),
    onSuccess: () => {
      if (expandedGroup) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenantGroups.members(expandedGroup),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.byCompany(tenantId) });
      setAddMemberGroupId(null);
      setMemberSearch("");
    },
  });

  const removeMemberMutation = useMutation({
    mutationFn: ({ groupId, memberId }: { groupId: string; memberId: string }) =>
      tenantGroupsApi.removeMember(tenantId, groupId, memberId),
    onSuccess: () => {
      if (expandedGroup) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.tenantGroups.members(expandedGroup),
        });
      }
      queryClient.invalidateQueries({ queryKey: queryKeys.tenantGroups.byCompany(tenantId) });
    },
  });

  const existingExternalIds = new Set(groups.map((g) => g.externalId).filter(Boolean));
  const filteredEntra = entraGroups.filter(
    (g) =>
      !existingExternalIds.has(g.externalId) &&
      g.displayName.toLowerCase().includes(importSearch.toLowerCase()),
  );

  const memberUserIds = new Set(members.map((m) => m.tenantUserId));
  const availableUsers = allUsers.filter(
    (u) =>
      !memberUserIds.has(u.id) &&
      (u.displayName?.toLowerCase().includes(memberSearch.toLowerCase()) ||
        u.email.toLowerCase().includes(memberSearch.toLowerCase())),
  );

  return (
    <CenterPanel title="Group Management">
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-text-secondary">
          {groups.length} group{groups.length !== 1 ? "s" : ""}
        </p>
        {isAdmin && (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={() => setShowImport(!showImport)}>
              <Download size={16} className="mr-1.5" />
              Import from Entra
            </Button>
            <Button size="sm" onClick={() => setShowCreateGroup(!showCreateGroup)}>
              <Plus size={16} className="mr-1.5" />
              Create Group
            </Button>
          </div>
        )}
      </div>

      {/* Create Group Form */}
      {showCreateGroup && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="text-sm">Create Local Group</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                placeholder="Group Name"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <input
                type="text"
                placeholder="Description (optional)"
                value={newGroupDesc}
                onChange={(e) => setNewGroupDesc(e.target.value)}
                className="rounded-md border border-border bg-surface px-3 py-2 text-sm"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={() => createMutation.mutate()}
                  disabled={!newGroupName}
                >
                  Create
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowCreateGroup(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Entra Import Panel */}
      {showImport && (
        <Card className="mb-4">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Download size={16} />
              Import Groups from Entra ID
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-3 text-xs text-text-muted">
              Importing a group will also import its members as users. Group membership will be synced from Entra.
            </p>
            <div className="mb-3 flex items-center gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="text"
                  placeholder="Search directory groups..."
                  value={importSearch}
                  onChange={(e) => setImportSearch(e.target.value)}
                  className="w-full rounded-md border border-border bg-surface pl-8 pr-3 py-2 text-sm"
                />
              </div>
              {selectedImports.size > 0 && (
                <Button size="sm" onClick={() => {
                  const selected = entraGroups.filter((g) => selectedImports.has(g.externalId));
                  importMutation.mutate(selected);
                }} disabled={importMutation.isPending}>
                  {importMutation.isPending ? (
                    <Loader2 size={14} className="mr-1.5 animate-spin" />
                  ) : (
                    <Check size={14} className="mr-1.5" />
                  )}
                  Import {selectedImports.size}
                </Button>
              )}
            </div>

            {entraLoading ? (
              <div className="flex items-center gap-2 py-4 text-sm text-text-muted">
                <Loader2 size={14} className="animate-spin" />
                Loading directory groups...
              </div>
            ) : filteredEntra.length === 0 ? (
              <p className="py-4 text-sm text-text-muted">
                {entraGroups.length === 0
                  ? "No groups found. Ensure GroupMember.Read.All permission has been granted."
                  : "All groups imported or no matches found."}
              </p>
            ) : (
              <div className="max-h-[300px] overflow-y-auto">
                {filteredEntra.map((group) => (
                  <label
                    key={group.externalId}
                    className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 hover:bg-hover"
                  >
                    <input
                      type="checkbox"
                      checked={selectedImports.has(group.externalId)}
                      onChange={() => {
                        setSelectedImports((prev) => {
                          const next = new Set(prev);
                          if (next.has(group.externalId)) next.delete(group.externalId);
                          else next.add(group.externalId);
                          return next;
                        });
                      }}
                      className="rounded border-border"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-text-primary">{group.displayName}</p>
                      {group.description && (
                        <p className="text-xs text-text-muted">{group.description}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Group List */}
      {isLoading ? (
        <p className="text-sm text-text-muted">Loading groups...</p>
      ) : groups.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <UsersRound size={40} className="mb-3 text-text-muted" />
            <p className="text-sm text-text-secondary">No groups yet.</p>
            <p className="text-xs text-text-muted mt-1">
              Import groups from Entra ID or create local groups.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {groups.map((group) => {
            const isExpanded = expandedGroup === group.id;
            return (
              <Card key={group.id}>
                <CardContent className="py-0">
                  {/* Group Header */}
                  <div
                    className="flex cursor-pointer items-center justify-between py-3"
                    onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                  >
                    <div className="flex items-center gap-3">
                      {isExpanded ? (
                        <ChevronDown size={16} className="text-text-muted" />
                      ) : (
                        <ChevronRight size={16} className="text-text-muted" />
                      )}
                      <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-accent/10">
                        <UsersRound size={18} className="text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{group.name}</p>
                        <p className="text-xs text-text-muted">
                          {group.memberCount} member{group.memberCount !== 1 ? "s" : ""}
                          {group.description && <span> &middot; {group.description}</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={group.source === "entra" ? "default" : "secondary"}>
                        {group.source === "entra" ? (
                          <><Cloud size={12} className="mr-1" />Entra</>
                        ) : (
                          <><HardDrive size={12} className="mr-1" />Local</>
                        )}
                      </Badge>
                      {group.source === "entra" && isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            syncMutation.mutate(group.id);
                          }}
                          disabled={syncMutation.isPending}
                          title="Sync membership from Entra"
                        >
                          <RefreshCw
                            size={14}
                            className={syncMutation.isPending ? "animate-spin" : ""}
                          />
                        </Button>
                      )}
                      {isAdmin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteMutation.mutate(group.id);
                          }}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Expanded: Member List */}
                  {isExpanded && (
                    <div className="border-t border-border pb-3 pt-2">
                      {/* Add member button */}
                      {isAdmin && group.source === "local" && (
                        <div className="mb-2">
                          {addMemberGroupId === group.id ? (
                            <div className="flex flex-col gap-2">
                              <div className="relative">
                                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                                <input
                                  type="text"
                                  placeholder="Search users to add..."
                                  value={memberSearch}
                                  onChange={(e) => setMemberSearch(e.target.value)}
                                  className="w-full rounded-md border border-border bg-surface pl-8 pr-3 py-1.5 text-sm"
                                />
                              </div>
                              {availableUsers.length > 0 && (
                                <div className="max-h-[200px] overflow-y-auto rounded-md border border-border">
                                  {availableUsers.slice(0, 10).map((user) => (
                                    <button
                                      key={user.id}
                                      className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-hover text-sm"
                                      onClick={() =>
                                        addMemberMutation.mutate({
                                          groupId: group.id,
                                          userIds: [user.id],
                                        })
                                      }
                                    >
                                      <UserPlus size={14} className="text-text-muted" />
                                      <span>{user.displayName || user.email}</span>
                                      <span className="text-xs text-text-muted ml-auto">
                                        {user.email}
                                      </span>
                                    </button>
                                  ))}
                                </div>
                              )}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => {
                                  setAddMemberGroupId(null);
                                  setMemberSearch("");
                                }}
                              >
                                <X size={14} className="mr-1" />
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setAddMemberGroupId(group.id)}
                            >
                              <UserPlus size={14} className="mr-1.5" />
                              Add Member
                            </Button>
                          )}
                        </div>
                      )}

                      {group.lastSyncedAt && (
                        <p className="mb-2 text-xs text-text-muted">
                          Last synced: {new Date(group.lastSyncedAt).toLocaleString()}
                        </p>
                      )}

                      {members.length === 0 ? (
                        <p className="py-2 text-xs text-text-muted">No members</p>
                      ) : (
                        <div className="flex flex-col gap-1">
                          {members.map((member) => (
                            <div
                              key={member.id}
                              className="flex items-center justify-between rounded-md px-3 py-1.5 hover:bg-hover"
                            >
                              <div>
                                <p className="text-sm text-text-primary">
                                  {member.userDisplayName || member.userEmail}
                                </p>
                                <p className="text-xs text-text-muted">{member.userEmail}</p>
                              </div>
                              <div className="flex items-center gap-2">
                                <Badge variant="secondary" className="text-xs">
                                  {member.userRole}
                                </Badge>
                                {isAdmin && group.source === "local" && (
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() =>
                                      removeMemberMutation.mutate({
                                        groupId: group.id,
                                        memberId: member.id,
                                      })
                                    }
                                  >
                                    <X size={14} />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </CenterPanel>
  );
}
